import { useState } from 'react'

import { ActionIcon, Collapse, Loader, Menu, Tooltip } from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import AvatarGroup from 'src/components/DataDisplay/AvatarGroup/AvatarGroup'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_COMPANY_TAGS,
  DELETE_COMPANY_TAG,
} from 'src/graphql/companyTags.graphql'
import { EmployeeStatus } from 'src/graphql/types/employees'
import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconCopy from 'src/icons/IconCopy'
import IconEdit from 'src/icons/IconEdit'
import IconThreeDots from 'src/icons/IconThreeDots'
import IconTrash from 'src/icons/IconTrash'
import { formatDate } from 'src/lib/formatters'

import CreateTagModal from './CreateTagModal'

interface TagsListProps {
  initialOpened?: boolean
}

const TagsList = ({ initialOpened = false }: TagsListProps) => {
  const [modalOpened, setModalOpened] = useState(false)
  const [editingTag, setEditingTag] = useState(null)
  const [deleteModalOpened, setDeleteModalOpened] = useState(false)
  const [tagToDelete, setTagToDelete] = useState(null)
  const [opened, setOpened] = useState(initialOpened)

  const {
    data: tagsData,
    loading,
    refetch,
  } = useQuery(GET_COMPANY_TAGS, {
    variables: {
      pageInfo: { limit: 100, offset: 0 },
    },
  })

  const tags = tagsData?.companyTags?.items || []

  const [deleteTag, { loading: deleting }] = useMutation(DELETE_COMPANY_TAG, {
    onCompleted: ({ deleteCompanyTag }) => {
      toast(`Tag "${deleteCompanyTag.label}" deleted`, 'success')
      refetch()
      setDeleteModalOpened(false)
      setTagToDelete(null)
    },
    onError: () => toast('Error deleting tag', 'error'),
  })

  const handleOpenCreateModal = () => {
    setEditingTag(null)
    setModalOpened(true)
    if (!opened) setOpened(true)
  }

  const handleOpenEditModal = (tag) => {
    setEditingTag(tag)
    setModalOpened(true)
  }

  const handleCloseModal = () => {
    setModalOpened(false)
    setEditingTag(null)
  }

  const handleOpenDeleteModal = (tag) => {
    setTagToDelete(tag)
    setDeleteModalOpened(true)
  }

  const handleConfirmDelete = async () => {
    if (tagToDelete) {
      await deleteTag({ variables: { tagId: tagToDelete.tagId } })
    }
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpened(false)
    setTagToDelete(null)
  }

  const handleCopyId = (tagId: string) => {
    navigator.clipboard.writeText(tagId)
    toast('Tag ID copied to clipboard', 'success')
  }

  const isAtLimit = tags.length >= 100

  return (
    <div className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3">
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Tags ({tags.length}/100)
        <Tooltip
          label="You have reached the maximum of 100 tags"
          disabled={!isAtLimit}
        >
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              handleOpenCreateModal()
            }}
            disabled={isAtLimit}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconAdd className="h-3.5 w-3.5 fill-none stroke-current" />
            Create
          </DNButton>
        </Tooltip>
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          {/* Loading/Empty/Table States */}
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader color="gray" size="xs" />
            </div>
          ) : tags.length === 0 ? (
            <div className="flex flex-col items-center gap-4 rounded-md border border-doubleNickel-gray-300 p-8 text-center">
              <div className="text-sm text-doubleNickel-gray-500">
                No tags configured yet
              </div>
              <DNButton
                variant="primary"
                size="sm"
                onClick={handleOpenCreateModal}
              >
                Create Your First Tag
              </DNButton>
            </div>
          ) : (
            <div className="overflow-auto rounded-md border border-doubleNickel-gray-300">
              <table className="w-full">
                <thead className="bg-doubleNickel-gray-50">
                  <tr className="border-b border-doubleNickel-gray-300">
                    <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                      ID
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                      Label
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                      Category
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                      Created By
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-doubleNickel-gray-700">
                      Created At
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-semibold text-doubleNickel-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag, index) => (
                    <tr
                      key={tag.tagId}
                      className={`${
                        index !== tags.length - 1
                          ? 'border-b border-doubleNickel-gray-200'
                          : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <Tooltip label={tag.tagId} withArrow>
                          <ActionIcon
                            variant="subtle"
                            size="sm"
                            color="gray"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCopyId(tag.tagId)
                            }}
                          >
                            <IconCopy className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
                          </ActionIcon>
                        </Tooltip>
                      </td>
                      <td className="px-4 py-3 text-sm text-doubleNickel-gray-900">
                        {tag.label}
                      </td>
                      <td className="px-4 py-3 text-sm text-doubleNickel-gray-700">
                        {tag.category || (
                          <span className="text-doubleNickel-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {tag.createdBy ? (
                          <AvatarGroup
                            avatars={[
                              {
                                firstName: tag.createdBy.firstName || '',
                                lastName: tag.createdBy.lastName || '',
                                isInactive:
                                  tag.createdBy.status !==
                                  EmployeeStatus.ACTIVE,
                              },
                            ]}
                          />
                        ) : (
                          <span className="text-sm text-doubleNickel-gray-400">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-doubleNickel-gray-700">
                        {tag.createdAt ? (
                          formatDate(tag.createdAt)
                        ) : (
                          <span className="text-doubleNickel-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Menu
                          withinPortal
                          position="bottom-end"
                          classNames={{
                            item: 'pl-0',
                          }}
                        >
                          <Menu.Target>
                            <ActionIcon variant="subtle" size="sm">
                              <IconThreeDots className="h-4 w-4" />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={
                                <IconEdit className="h-4 w-4 fill-none" />
                              }
                              onClick={() => handleOpenEditModal(tag)}
                            >
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              leftSection={
                                <IconTrash className="h-4 w-4 fill-none" />
                              }
                              color="red"
                              onClick={() => handleOpenDeleteModal(tag)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Collapse>

      {/* Modals */}
      <CreateTagModal
        tag={editingTag}
        isEdit={!!editingTag}
        opened={modalOpened}
        onClose={handleCloseModal}
        onSuccess={() => refetch()}
      />

      <DeleteModal
        title="Delete Tag"
        description={`Are you sure you want to delete the tag "${tagToDelete?.label}"? This action cannot be undone.`}
        opened={deleteModalOpened}
        close={handleCloseDeleteModal}
        onDelete={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  )
}

export default TagsList
